/**
 * Drive sync hooks — TanStack Query wrappers around ``driveManagement``
 * adapter + the unified ``SyncStore``.
 *
 * Drive's UX is the most complex of the three sync families because the
 * user picks WHICH folders + files to track BEFORE triggering sync. The
 * ``useTrackedContent`` + ``useAddTrackedFolder`` family of hooks owns
 * that selection state; the ``useDriveSyncTrigger`` family then kicks
 * the ingestion worker for the current selection.
 *
 * Public surface (used by ``DocSyncManager``, ``AddFolderDialog``,
 * ``$capability``, ``ConnectedAccountRow``):
 *
 *   - ``useTrackedContent``         — folders + standalone files
 *   - ``useFolderContents``         — per-folder new/modified/synced delta
 *   - ``useDriveFolderTree``        — folder tree for the picker
 *   - ``useAddTrackedFolder`` / ``useRemoveTrackedFolder``
 *   - ``useSelectTrackedFiles`` / ``useRemoveTrackedFile``
 *   - ``useDriveSyncStatus``        — single-account status query
 *   - ``useDriveSyncTrigger``       — POST start-ingestion (default: tracked surface)
 *   - ``useDriveForceSync``         — POST force-sync (full re-index)
 *   - ``useSyncDriveFile``          — single-file resync
 *   - ``useDriveSyncCancel``        — POST cancel-sync
 */

import {
  type DriveProvider,
  type DriveSyncStatus,
  type FolderContents,
  type FolderTreeResponse,
  type DriveStartIngestionBody,
  type DriveSyncJobAck,
  type NotionPickablePage,
  type SharePointDrive,
  type SharePointSite,
  type TrackedContentResponse,
  addTrackedFolder,
  cancelDriveSync,
  forceDriveSync,
  getDriveFolderTree,
  getDriveSyncStatus,
  getFolderContents,
  getTrackedContent,
  listSharePointDrives,
  listSharePointFiles,
  listSharePointSites,
  removeTrackedFile,
  removeTrackedFolder,
  searchNotionWorkspace,
  selectTrackedFiles,
  selectTrackedFolders,
  sharePointFilesToFolderTree,
  startDriveIngestion,
  syncDriveFile,
} from "@ando/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLingui } from "@lingui/react/macro";

import { useChatUserId } from "@/features/chat/hooks/useChatSession";
import { useSyncStore } from "@/features/sync/state/syncStore";

// ──────────────────────────────────────────────────────────────────────
// Query keys
// ──────────────────────────────────────────────────────────────────────

export const driveQueryKeys = {
  /**
   * Root prefix — passare a ``queryClient.invalidateQueries`` per
   * invalidare l'INTERA subtree drive (tracked + folder-contents +
   * folder-tree + status). Usato dal pulsante "Aggiorna" globale del
   * DocSyncManager (giugno 11, 2026). Evita lo string-literal magic
   * sparso nei consumer.
   */
  all: ["driveManagement"] as const,
  trackedContent: (uid: string, p: DriveProvider, k?: string) =>
    ["driveManagement", "trackedContent", uid, p, k ?? ""] as const,
  folderContents: (folderId: string) =>
    ["driveManagement", "folderContents", folderId] as const,
  folderTree: (
    uid: string,
    p: DriveProvider,
    k?: string,
    root?: string,
    forceRefresh = false,
  ) =>
    [
      "driveManagement",
      "folderTree",
      uid,
      p,
      k ?? "",
      root ?? "",
      forceRefresh ? "force" : "cache",
    ] as const,
  status: (uid: string, p: DriveProvider, k?: string) =>
    ["driveManagement", "status", uid, p, k ?? ""] as const,
} as const;

// Tracked-content è una lista DB-only che cambia solo quando l'utente aggiunge/rimuove
// folder o file dal tracking. 5 min di stale evita il flash "Carico i tuoi file…" ad
// ogni riapertura del DocSyncManager. Le mutation invalidano già la query esplicitamente.
const TRACKED_STALE_MS = 5 * 60_000;
const STATUS_POLL_MS = 5_000;
const TREE_STALE_MS = 60_000;

// Sync job statuses that warrant continued polling. Terminal states
// (completed/failed/cancelled) return `false` from the refetchInterval callback
// below so the poll goes idle and stops firing background fetches — otherwise
// the shared `useIsFetching(driveQueryKeys.all)` selector in DocSyncManager
// keeps flashing "Aggiornamento…" forever even when nothing is happening.
const ACTIVE_JOB_STATUSES = new Set([
  "pending",
  "processing",
  "running",
  "retrying",
  "cancelling",
]);

// ──────────────────────────────────────────────────────────────────────
// Read hooks
// ──────────────────────────────────────────────────────────────────────

export function useTrackedContent(
  provider: DriveProvider,
  connectionKey?: string,
) {
  const userId = useChatUserId();
  return useQuery<TrackedContentResponse>({
    queryKey: driveQueryKeys.trackedContent(
      userId ?? "",
      provider,
      connectionKey,
    ),
    queryFn: () =>
      getTrackedContent(userId as string, provider, connectionKey),
    enabled: !!userId,
    staleTime: TRACKED_STALE_MS,
  });
}

/**
 * SharePoint folder tree — V1-parity fetch via ``/drive/files`` (not
 * ``/drive-management/folder-tree-cached``, which the BE rejects for
 * SharePoint with a ValidationError). Maps the flat ``SharePointFile[]``
 * response into the shared ``FolderTreeResponse`` shape so the picker
 * UI is mode-agnostic. June 5 2026 — V2 SharePoint picker was wired to
 * the rejected route, so step 3 never rendered a folder.
 */
export function useSharePointFolderTree(
  siteId: string | null,
  driveId: string | null,
  connectionKey?: string,
  enabled = true,
) {
  const userId = useChatUserId();
  return useQuery<FolderTreeResponse>({
    queryKey: [
      "driveManagement",
      "sharepoint",
      "folderTree",
      userId ?? "",
      siteId ?? "",
      driveId ?? "",
      connectionKey ?? "",
    ] as const,
    queryFn: async () => {
      const raw = await listSharePointFiles(
        userId as string,
        siteId as string,
        driveId ?? undefined,
        undefined,
        connectionKey,
      );
      return sharePointFilesToFolderTree(raw);
    },
    enabled: enabled && !!userId && !!siteId && !!driveId,
    staleTime: TREE_STALE_MS,
  });
}

export function useFolderContents(
  folderId: string | null,
  provider: DriveProvider,
  connectionKey?: string,
) {
  const userId = useChatUserId();
  return useQuery<FolderContents>({
    queryKey: driveQueryKeys.folderContents(folderId ?? ""),
    queryFn: () =>
      getFolderContents({
        userId: userId as string,
        provider,
        folderId: folderId as string,
        connectionKey,
      }),
    enabled: !!folderId && !!userId,
    staleTime: 15_000,
  });
}

export function useDriveFolderTree(
  provider: DriveProvider,
  connectionKey?: string,
  root?: string,
  enabled = true,
  forceRefresh = false,
) {
  const userId = useChatUserId();
  return useQuery<FolderTreeResponse>({
    queryKey: driveQueryKeys.folderTree(
      userId ?? "",
      provider,
      connectionKey,
      root,
      forceRefresh,
    ),
    queryFn: () =>
      getDriveFolderTree(
        userId as string,
        provider,
        connectionKey,
        root,
        forceRefresh,
      ),
    enabled: enabled && !!userId,
    staleTime: TREE_STALE_MS,
  });
}

export function useDriveSyncStatus(
  provider: DriveProvider,
  connectionKey?: string,
): DriveSyncStatus | null {
  const userId = useChatUserId();
  const { data } = useQuery({
    queryKey: driveQueryKeys.status(userId ?? "", provider, connectionKey),
    queryFn: () =>
      getDriveSyncStatus(userId as string, provider, connectionKey),
    enabled: !!userId,
    // Poll ONLY while a sync is genuinely active — the shared
    // `useIsFetching(driveQueryKeys.all)` selector in DocSyncManager was
    // otherwise flashing "Aggiornamento…" every 5s indefinitely because
    // this query kept refetching forever even in idle state.
    refetchInterval: (query) => {
      const status = query.state.data?.job_status;
      return status && ACTIVE_JOB_STATUSES.has(status) ? STATUS_POLL_MS : false;
    },
  });
  return data ?? null;
}

// ──────────────────────────────────────────────────────────────────────
// Tracked-content mutations (folder/file selection)
// ──────────────────────────────────────────────────────────────────────

export interface AddTrackedFolderVars {
  readonly provider: DriveProvider;
  readonly remoteId: string;
  readonly name: string;
  readonly connectionKey?: string;
}

function invalidateTrackedFor(
  qc: ReturnType<typeof useQueryClient>,
  userId: string,
  provider: DriveProvider,
  connectionKey?: string,
): void {
  void qc.invalidateQueries({
    queryKey: driveQueryKeys.trackedContent(userId, provider, connectionKey),
  });
}

export function useAddTrackedFolder() {
  const { t } = useLingui();
  const userId = useChatUserId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: AddTrackedFolderVars) => {
      if (!userId) throw new Error(t`Sessione utente assente`);
      return addTrackedFolder({
        user_id: userId,
        provider: vars.provider,
        remote_id: vars.remoteId,
        name: vars.name,
        connection_key: vars.connectionKey,
      });
    },
    onSuccess: (_data, vars) => {
      if (userId) {
        invalidateTrackedFor(qc, userId, vars.provider, vars.connectionKey);
      }
    },
  });
}

export interface RemoveTrackedVars {
  readonly id: string;
  readonly provider: DriveProvider;
  readonly connectionKey?: string;
}

export function useRemoveTrackedFolder() {
  const { t } = useLingui();
  const userId = useChatUserId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: RemoveTrackedVars) => {
      // June 11, 2026 — BE richiede ``user_id`` + ``provider`` come
      // query params. Pre-fix passavamo solo ``id`` → 422.
      //
      // Audit (giugno 11, 2026): assert userId esplicito invece di
      // ``userId ?? ""``. Una stringa vuota verrebbe spedita al BE
      // che farebbe 422 silente "user_id missing" — UX poco PRO
      // perché l'errore arriva via toast generico. ``useChatUserId``
      // è sempre presente quando il drawer è aperto (è gated dalla
      // session), quindi il throw qui è defense-in-depth contro un
      // bug futuro che permettesse di aprire il drawer senza session.
      if (!userId) throw new Error("Sessione utente non disponibile.");
      await removeTrackedFolder({
        folderId: vars.id,
        userId,
        provider: vars.provider,
        connectionKey: vars.connectionKey,
      });
    },
    onSuccess: (_data, vars) => {
      if (userId) {
        invalidateTrackedFor(qc, userId, vars.provider, vars.connectionKey);
      }
    },
  });
}

export interface SelectTrackedFilesVars {
  readonly provider: DriveProvider;
  readonly connectionKey?: string;
  /**
   * One row per file the picker returned. Field names match the BE
   * Pydantic model (drive_management.py:878) — ``file_size`` not
   * ``size_bytes``. SharePoint requires ``site_id`` + ``drive_id``.
   */
  readonly selections: readonly {
    readonly file_id: string;
    readonly file_name: string;
    readonly file_path?: string;
    readonly parent_folder_id?: string | null;
    readonly mime_type?: string;
    readonly file_size?: number;
    readonly web_view_link?: string;
    readonly owner?: string;
    readonly modified_time?: string;
    readonly site_id?: string;
    readonly drive_id?: string;
  }[];
}

export function useSelectTrackedFiles() {
  const userId = useChatUserId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: SelectTrackedFilesVars) => {
      if (!userId) throw new Error("Sessione utente assente");
      return selectTrackedFiles({
        user_id: userId,
        provider: vars.provider,
        connection_key: vars.connectionKey,
        selections: vars.selections,
      });
    },
    onSuccess: (_data, vars) => {
      if (userId) {
        invalidateTrackedFor(qc, userId, vars.provider, vars.connectionKey);
      }
    },
  });
}

export interface SelectTrackedFoldersVars {
  readonly provider: DriveProvider;
  readonly connectionKey?: string;
  /**
   * One row per folder. ``folder_path`` is REQUIRED on the BE side
   * (drive_management.py:862) — callers fall back to the folder name
   * or remote id when the picker didn't expose a path.
   */
  readonly selections: readonly {
    readonly folder_id: string;
    readonly folder_path: string;
    readonly folder_name?: string;
    readonly pre_embed?: boolean;
    readonly site_id?: string;
    readonly drive_id?: string;
  }[];
}

/**
 * Mutation — POST ``/drive-management/select-folders`` (bulk).
 *
 * Companion to ``useSelectTrackedFiles``: the V1 picker callback hands
 * back BOTH folders + files in a single ``PickedItem[]`` array; the FE
 * splits them by ``isFolder`` and fires the two mutations in parallel.
 */
export function useSelectTrackedFolders() {
  const userId = useChatUserId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: SelectTrackedFoldersVars) => {
      if (!userId) throw new Error("Sessione utente assente");
      return selectTrackedFolders({
        user_id: userId,
        provider: vars.provider,
        connection_key: vars.connectionKey,
        selections: vars.selections,
      });
    },
    onSuccess: (_data, vars) => {
      if (userId) {
        invalidateTrackedFor(qc, userId, vars.provider, vars.connectionKey);
      }
    },
  });
}

export function useRemoveTrackedFile() {
  const userId = useChatUserId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: RemoveTrackedVars) => {
      // Stesso contratto di useRemoveTrackedFolder — vedi quel commento.
      if (!userId) throw new Error("Sessione utente non disponibile.");
      await removeTrackedFile({
        fileId: vars.id,
        userId,
        provider: vars.provider,
        connectionKey: vars.connectionKey,
      });
    },
    onSuccess: (_data, vars) => {
      if (userId) {
        invalidateTrackedFor(qc, userId, vars.provider, vars.connectionKey);
      }
    },
  });
}

// ──────────────────────────────────────────────────────────────────────
// Sync mutations
// ──────────────────────────────────────────────────────────────────────

export interface DriveSyncTriggerVars {
  readonly provider: DriveProvider;
  readonly connectionKey?: string;
  /** When both empty → BE syncs the whole tracked surface. */
  readonly folderIds?: readonly string[];
  readonly fileIds?: readonly string[];
}

function buildIngestionBody(
  userId: string,
  vars: DriveSyncTriggerVars,
): DriveStartIngestionBody {
  return {
    user_id: userId,
    provider: vars.provider,
    connection_key: vars.connectionKey,
    folder_ids: vars.folderIds,
    file_ids: vars.fileIds,
  };
}

// ``useDriveSyncTrigger`` (incremental) and ``useDriveForceSync`` (full
// re-index) share the SAME mutation envelope — only the BE call differs.
// Factor the boilerplate so adding a 3rd flavour (e.g. ``/dry-run``)
// becomes a 1-line addition instead of a 25-line copy. Reviewer #10
// (June 4 2026) — pre-fix the two hooks duplicated track-job + onSuccess
// invalidation verbatim.
type DriveSyncBackendCall = (body: DriveStartIngestionBody) => Promise<DriveSyncJobAck>;

function useDriveSyncMutation(starter: DriveSyncBackendCall) {
  const userId = useChatUserId();
  const qc = useQueryClient();
  const trackJob = useSyncStore((s) => s.trackJob);
  return useMutation({
    mutationFn: async (vars: DriveSyncTriggerVars) => {
      if (!userId) throw new Error("Sessione utente assente");
      const res = await starter(buildIngestionBody(userId, vars));
      if (res.job_id) {
        trackJob(res.job_id, "docs", {
          user_id: userId,
          provider: vars.provider,
          connection_key: vars.connectionKey,
        });
      }
      return res;
    },
    // Invalidate ``trackedContent`` so the drawer reflects "synced"
    // status after the BE job lands — without this, the cache stayed
    // stale until the next ``staleTime`` window and the user saw "1
    // file da sincronizzare" persistent even after the sync completed.
    onSuccess: (_, vars) => {
      if (!userId) return;
      invalidateTrackedFor(qc, userId, vars.provider, vars.connectionKey);
    },
  });
}

/** Mutation — POST ``/drive-management/start-ingestion`` (incremental). */
export function useDriveSyncTrigger() {
  return useDriveSyncMutation(startDriveIngestion);
}

/** Mutation — POST ``/drive-management/force-sync`` (full re-index). */
export function useDriveForceSync() {
  return useDriveSyncMutation(forceDriveSync);
}

export interface SyncDriveFileVars {
  readonly provider: DriveProvider;
  readonly fileId: string;
  readonly connectionKey?: string;
}

/** Mutation — POST ``/drive-management/sync-file`` (single file). */
export function useSyncDriveFile() {
  const userId = useChatUserId();
  const trackJob = useSyncStore((s) => s.trackJob);
  return useMutation({
    mutationFn: async (vars: SyncDriveFileVars) => {
      if (!userId) throw new Error("Sessione utente assente");
      const res = await syncDriveFile({
        user_id: userId,
        provider: vars.provider,
        file_id: vars.fileId,
        connection_key: vars.connectionKey,
      });
      if (res.job_id) {
        trackJob(res.job_id, "docs", {
          user_id: userId,
          provider: vars.provider,
          connection_key: vars.connectionKey,
        });
      }
      return res;
    },
  });
}

export function useDriveSyncCancel() {
  const userId = useChatUserId();
  const cancelInStore = useSyncStore((s) => s.cancelJob);
  return useMutation({
    mutationFn: async (jobId: string) => {
      if (!userId) throw new Error("Sessione utente assente");
      void cancelInStore(jobId);
      return cancelDriveSync(jobId, userId);
    },
  });
}

// ──────────────────────────────────────────────────────────────────────
// Native-picker helpers
// ──────────────────────────────────────────────────────────────────────

// Google Drive uses Google's new "Picker API for desktop and mobile
// apps" (beta, April 2026) via ``initDrivePicker`` /
// ``exchangeDrivePicker`` in ``@ando/core`` — the legacy
// ``useGooglePickerToken`` mutation was removed (May 2026) because the
// iframe-based ``apis.google.com/picker`` SDK can't run inside Tauri's
// production WKWebView (no registrable JS origin).

export function useSharePointSites(connectionKey?: string, enabled = true) {
  const userId = useChatUserId();
  return useQuery({
    queryKey: [
      "driveManagement",
      "sharepoint",
      "sites",
      userId ?? "",
      connectionKey ?? "",
    ],
    queryFn: () =>
      listSharePointSites(userId as string, connectionKey),
    enabled: enabled && !!userId,
    staleTime: 5 * 60_000,
  });
}

export function useSharePointDrives(
  siteId: string | null,
  connectionKey?: string,
) {
  const userId = useChatUserId();
  return useQuery({
    queryKey: [
      "driveManagement",
      "sharepoint",
      "drives",
      userId ?? "",
      siteId ?? "",
      connectionKey ?? "",
    ],
    queryFn: () =>
      listSharePointDrives(userId as string, siteId as string, connectionKey),
    enabled: !!userId && !!siteId,
    staleTime: 5 * 60_000,
  });
}

export function useNotionWorkspacePages(search?: string, enabled = true) {
  const userId = useChatUserId();
  return useQuery({
    queryKey: [
      "driveManagement",
      "notion",
      "pages",
      userId ?? "",
      search ?? "",
    ],
    queryFn: () => searchNotionWorkspace(userId as string, search),
    enabled: enabled && !!userId,
    staleTime: 30_000,
  });
}

// Re-export common types so callers don't need a second import.
export type {
  DriveProvider,
  DriveSyncStatus,
  FolderContents,
  FolderTreeResponse,
  NotionPickablePage,
  SharePointDrive,
  SharePointSite,
  TrackedContentResponse,
};
